import {useMemo} from 'react';

import {WORKSPACE_PLACEHOLDER_NAME} from '../../../state/workspaces/displayNames';
import type {NamedWorkspaceMenuItem, SortOption} from '../types';
import {getMenuItems} from './useMenuItems';

/**
 * A React hook that processes and filters shared views based on various criteria.
 *
 * @param sharedViews - Object containing all shared views
 * @param viewerUsername - Current user's username
 * @param sortOption - How to sort the views ('lastSaved', 'lastCreated', or 'alphabetical')
 * @param shouldShowMyViewsOnly - If true, only shows views owned by the current user
 * @param searchQuery - Filters views by matching display name or username
 * @returns Array of filtered and sorted menu items for shared views
 *
 * The hook performs the following operations:
 * 1. Converts shared views into menu items
 * 2. Sorts items based on the specified sort option
 * 3. Optionally filters to show only the current user's views
 * 4. Optionally filters based on a search query (matches display name or username)
 */
export function useSharedViewsItems(
  sharedViews: Record<string, any>,
  viewerUsername: string | undefined,
  sortOption: SortOption,
  shouldShowMyViewsOnly: boolean = false,
  searchQuery: string = ''
): NamedWorkspaceMenuItem[] {
  return useMemo(() => {
    const sharedViewItems = getMenuItems(
      'shared',
      Object.values(sharedViews),
      viewerUsername
    );

    const sortedItems = sharedViewItems.sort((a, b) => {
      switch (sortOption) {
        case 'lastSaved':
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        case 'lastCreated':
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case 'alphabetical': {
          const nameA = a.displayName || WORKSPACE_PLACEHOLDER_NAME;
          const nameB = b.displayName || WORKSPACE_PLACEHOLDER_NAME;
          return nameA.localeCompare(nameB);
        }
        default:
          return 0;
      }
    });

    const filteredItems = shouldShowMyViewsOnly
      ? sortedItems.filter(item => item.username === viewerUsername)
      : sortedItems;

    return searchQuery
      ? filteredItems.filter(item => {
          const displayName = item.displayName || WORKSPACE_PLACEHOLDER_NAME;
          return (
            displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.username.toLowerCase().includes(searchQuery.toLowerCase())
          );
        })
      : filteredItems;
  }, [
    sharedViews,
    viewerUsername,
    sortOption,
    shouldShowMyViewsOnly,
    searchQuery,
  ]);
}
